// Deliverable / Obligation tab — orchestrates list + CRUD + archive +
// the Generate Occurrences workflow for deliverable tracking items
// under a contract. Uses the grouped DeliverableTable (parent /
// child / one-off) and preserves earlier-phase behaviours: F3
// archived-item deep-link banner, F5 post-Create row highlight, N1
// includeArchived invariant.
//
// Row interaction (F3): row click opens a read-only DetailExpand;
// the pencil action opens the editable InlineExpand. Clicking a
// view-expanded row again collapses it; clicking a different row
// moves the expansion. Parent rows are expand-only (chevron) and
// do not open the detail view.
//
// Filtering pipeline: summary-card filter →
// chip-bar filter → text search → sort → paginate. SummaryStrip
// counts read from the unfiltered contract-wide deliverable set so
// they remain the source of truth even when chips narrow the table.
//
// URL state: filters / sort / page round-trip through
// useSearchParams. ?page=99 on a 2-page list silently clamps to 2.
//
// RAG auto-change banner: prevItem is captured from caller scope
// (the row object already in hand) immediately before each lifecycle
// mutation. The React Query item-detail cache is NOT used as the
// primary source because it is only populated for rows the user has
// already expanded — cache-only capture would silently no-op for
// collapsed-row actions.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import Alert from "../../../common/Alert";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import TrackingTabShell from "../shared/TrackingTabShell";
import ArchiveDialog from "../shared/ArchiveDialog";
import DeliverableFormModal, {
  type DeliverableFormMode as FormModalMode,
} from "./DeliverableFormModal";
import DeliverableInlineExpand from "./DeliverableInlineExpand";
import DeliverableDetailExpand from "./DeliverableDetailExpand";
import { lookupOccurrenceContext } from "./lookupOccurrenceContext";
import DeliverableDetailModal from "./DeliverableDetailModal";
import AcceptanceGateDialog, {
  type DeliverableDerivedLifecycleStatus,
} from "./AcceptanceGateDialog";
import { useAuth } from "../../../../contexts/AuthContext";
import DeliverableTable, {
  type DeliverableAction,
  type DeliverableSortColumn,
  type DeliverableSortDirection,
} from "./DeliverableTable";
import GenerateOccurrencesDialog from "./GenerateOccurrencesDialog";
import SummaryStrip from "./SummaryStrip";
import { deriveRagBannerReason } from "./deriveRagBannerReason";
import MarkDeliverableDatePopover, {
  type MarkAction,
} from "./MarkDeliverableDatePopover";
import FilterChipBar from "./FilterChipBar";
import ActiveFilterSummary from "./ActiveFilterSummary";
import { AlertTriangle, Download } from "lucide-react";
import Button from "../../../common/Button";
import {
  buildDeliverablesCsv,
  deliverablesCsvFilename,
} from "./deliverablesCsv";
import { formatSowDisplayNumber } from "../milestone/formatSowDisplayNumber";
import TrackingPagination from "../shared/TrackingPagination";
import { useDeepLinkPageJump } from "../shared/useDeepLinkPageJump";
import { useTableSort } from "../shared/useTableSort";
import { useSowScopedCreateAction } from "../shared/useSowScopedCreateAction";
import { applyChipFilters } from "./applyChipFilters";
import { sortRows } from "./sortRows";
import { groupDeliverables, type Row } from "./groupDeliverables";
import { findDeliverableDeepLinkTarget } from "./deliverableDeepLink";
import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import { groupRowsByStatus } from "./deliverableGrouping";
import DeliverableGroupedView from "./DeliverableGroupedView";
import OccurrenceStripKey from "./OccurrenceStripKey";
import TableViewToggle from "../milestone/TableViewToggle";
import {
  applySummaryFilter,
  type SummaryFilterKey,
} from "./applySummaryFilter";
import {
  parseDeliverableUrlState,
  serialiseDeliverableUrlState,
} from "./deliverableUrlState";
import ScopeSelector from "../shared/ScopeSelector";
import { applyScopeFilter } from "../shared/applyScopeFilter";
import { ALL_SCOPE, type ScopeValue } from "../shared/scopeTypes";
import {
  shouldAutoOpenOnCreate,
  shouldAutoOpenOnUpdate,
} from "./serialiseRule";
import {
  useAllDeliverablesForSummary,
  useArchiveTrackingItem,
  useCreateTrackingItem,
  useGenerateOccurrences,
  useTrackingItem,
  useTrackingItems,
  useUpdateTrackingItem,
} from "../../../../hooks/useTracking";
import { useManagedContract } from "../../../../hooks/useManagedContracts";
import type {
  CreateDeliverableInput,
  Deliverable,
  DeliverableLifecycleEvent,
  Milestone,
  RagRating,
  Sow,
  TrackingItem,
  UpdateDeliverableInput,
} from "../../../../types";

interface DeliverableTabProps {
  contractId: string;
  itemIdFromUrl?: string | null;
  /** Fired when the user clicks the "Linked milestone" pill on a
   *  deliverable's detail view. Threaded to DeliverableDetailExpand;
   *  parent wires it to a URL mutation that switches to the milestone
   *  tab and expands the row. */
  onNavigateToMilestone?: (milestoneId: string) => void;
}

// B4: human-readable sort-column labels for the aria-live announcer.
// "Due" → "Due date" so the spoken announcement reads naturally even
// for screen-reader users; the column header itself stays the
// shorter "Due" to keep the table scannable.
const SORT_COLUMN_LABELS: Record<DeliverableSortColumn, string> = {
  name: "Name",
  owner: "Owner",
  due: "Due date",
  status: "Status",
  rag: "RAG",
};

// deriveRagBannerReason lives in its own leaf module so it can be unit-tested
// directly — see ./deriveRagBannerReason.ts and its test.

// Bridge: map the deliverable's derived status to the gate dialog's
// status union. `late` shares submitted's outcome set (delayed delivery
// still needs the same accept/conditional/fail/reject decision).
// Non-gate-applicable statuses (not-started, overdue, revision-requested)
// don't reach this helper — handleAction guards on those before
// triggering the dialog. `revision-requested` is left out of the gate
// because it's the supplier rework queue, not customer review.
function toAcceptanceGateStatus(
  d: Deliverable,
): DeliverableDerivedLifecycleStatus {
  const derived = deriveDeliverableStatus(
    d.status,
    d.dueDate,
    new Date(),
    d.submittedAt,
  );
  if (derived === "late") return "submitted";
  if (
    derived === "submitted" ||
    derived === "under-review" ||
    derived === "conditionally-accepted" ||
    derived === "awaiting-resubmission" ||
    derived === "accepted" ||
    derived === "rejected" ||
    derived === "closed"
  ) {
    return derived;
  }
  // Unreachable — handleAction filters out not-started / overdue /
  // revision-requested before opening the dialog. Default to a safe
  // pass-through that the gate handles as terminal-only.
  return "closed";
}

// Minimum legal date for a new lifecycle event — the latest existing
// event's date, or the submittedAt date (if present), or undefined.
// Mirrors the backend chronological-consistency guard so the date
// input picker constrains the user instead of letting them post a
// back-dated event that the server would reject.
function deriveAcceptanceGateMinDate(d: Deliverable): string | undefined {
  const events = d.lifecycleEvents ?? [];
  if (events.length > 0) {
    let maxDate = events[0].date;
    for (let i = 1; i < events.length; i++) {
      if (events[i].date > maxDate) maxDate = events[i].date;
    }
    return maxDate;
  }
  return d.submittedAt?.slice(0, 10) ?? undefined;
}

export default function DeliverableTab({
  contractId,
  itemIdFromUrl,
  onNavigateToMilestone,
}: DeliverableTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(
    () => parseDeliverableUrlState(searchParams),
    [searchParams],
  );

  const updateUrl = useCallback(
    (mutate: (s: typeof urlState) => typeof urlState) => {
      const next = mutate(urlState);
      setSearchParams(serialiseDeliverableUrlState(next, searchParams), {
        replace: true,
      });
    },
    [searchParams, setSearchParams, urlState],
  );

  // sr-only sort announcement string. Updated whenever the user
  // re-sorts a column so screen-reader users get a 'polite' nudge.
  const [sortAnnouncement, setSortAnnouncement] = useState<string>("");

  // Sort state round-trips through the URL (?sort=&dir=) rather than local
  // React state — the `persisted` adapter lets this tab share the same
  // SortState<Column> shape and semantics as Insurance/Milestone while
  // keeping the URL as the source of truth. The per-column cycling itself
  // is computed by SortIndicator (DeliverableTable's sortable headers), so
  // this tab only needs the read/write container, not handleSortClick.
  const deliverableSort = useTableSort<DeliverableSortColumn>(
    { column: null, direction: null, cleared: false },
    {
      persisted: {
        value: {
          column: urlState.sortColumn,
          direction: urlState.sortDirection,
          cleared: false,
        },
        onChange: (next) => {
          updateUrl((s) => ({
            ...s,
            sortColumn: next.column,
            sortDirection: next.direction,
            page: 1,
          }));
          // B4: announce the sort change to assistive tech via the
          // sr-only aria-live region. "Sort cleared" when the user
          // cycles past descending, otherwise "Sorted by <Label>,
          // <direction>".
          if (!next.column || !next.direction) {
            setSortAnnouncement("Sort cleared");
          } else {
            const label = SORT_COLUMN_LABELS[next.column];
            setSortAnnouncement(`Sorted by ${label}, ${next.direction}`);
          }
        },
      },
    },
  );
  // Bumped by handleClearAll so the chip-bar's local search-input state
  // forcibly resyncs with the prop even when the prop value didn't
  // visibly change (mid-debounce reset race). Without this, typing then
  // immediately Clear-all'ing leaves the input populated because the
  // typed text never reached the parent's `search` state.
  const [filterResetCounter, setFilterResetCounter] = useState(0);

  const [modalMode, setModalMode] = useState<FormModalMode | null>(null);
  const [modalSource, setModalSource] = useState<Deliverable | undefined>();
  // Pre-fill SOW id consumed from `?action=create&sowId=<id>` for the
  // cross-tab "Add deliverable to this SOW" flow. Read once on mount,
  // then the URL params are stripped so a refresh doesn't re-trigger.
  const [pendingCreateSowId, setPendingCreateSowId] = useState<string | null>(
    null,
  );

  // F3: view-mode and edit-mode expansions are independent states.
  // Row click toggles view; the pencil action switches view → edit
  // (or opens edit directly when no row is expanded).
  const [viewExpandedItem, setViewExpandedItem] = useState<Deliverable | null>(
    null,
  );
  const [inlineEditItem, setInlineEditItem] = useState<Deliverable | null>(
    null,
  );
  // Parent-schedule edit switches DeliverableInlineExpand into
  // metadata-editable mode (recurrence rule editable, per-occurrence
  // fields hidden).
  const [inlineEditIsParent, setInlineEditIsParent] = useState(false);

  // Expanded parent ids live in a Set so multiple parents can be
  // open at once. Default empty = all collapsed.
  const [expandedScheduleIds, setExpandedScheduleIds] = useState<Set<string>>(
    () => new Set(),
  );
  // When a summary-card filter is applied, remember the pre-filter
  // expansion snapshot so clearing the filter restores it.
  const [
    preFilterExpandedScheduleIds,
    setPreFilterExpandedScheduleIds,
  ] = useState<Set<string> | null>(null);
  // F6: same snapshot pattern but for the chip-bar filter pathway —
  // chip activation auto-expands parents with matching children
  // (mirroring the SummaryStrip card behaviour); chip clear restores
  // the user's pre-chip-filter expansion set.
  const [
    preChipFilterExpandedScheduleIds,
    setPreChipFilterExpandedScheduleIds,
  ] = useState<Set<string> | null>(null);
  const [activeFilter, setActiveFilter] = useState<SummaryFilterKey | null>(
    null,
  );

  const [archiveTarget, setArchiveTarget] = useState<Deliverable | null>(null);
  const [highlightRowId, setHighlightRowId] = useState<string | null>(
    itemIdFromUrl ?? null,
  );
  const [generateTarget, setGenerateTarget] = useState<Deliverable | null>(
    null,
  );
  const [detailItem, setDetailItem] = useState<Deliverable | null>(null);
  const [archiveBanner, setArchiveBanner] = useState(true);
  // Confirmation popover for the three deliverable status transitions
  // that carry an effectiveDate (Submit / Accept / Reject). Stored as
  // a single state slot so only one popover is open at a time.
  const [markPopover, setMarkPopover] = useState<{
    item: Deliverable;
    action: MarkAction;
  } | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);
  // Acceptance-gate dialog target — the row whose primary action opens
  // the gate. Routing matrix: not-started/overdue stay on the legacy
  // mark-popover ("Mark submitted"); submitted/under-review/late/
  // conditionally-accepted/awaiting-resubmission and the three terminal
  // states route to the gate via this state slot.
  const [acceptanceGateTarget, setAcceptanceGateTarget] =
    useState<Deliverable | null>(null);
  const { user: authUser } = useAuth();

  // Tab-local aria-live banner for RAG auto-changes. Captures prevItem
  // from caller scope before each lifecycle mutation so the banner can
  // be computed without touching the React Query detail cache (which is
  // only populated for rows the user has already opened, so cache-only
  // capture would silently no-op for collapsed-row actions).
  const [ragBanner, setRagBanner] = useState<{ message: string } | null>(null);
  // Row flash: id of the row whose background should flash, plus the RAG
  // rating from the mutation response (the list query may not have caught up
  // yet, so reading ragRating from the stale row data would produce no flash).
  const [recentlyAutoChangedId, setRecentlyAutoChangedId] = useState<string | null>(null);
  const [recentlyAutoChangedRag, setRecentlyAutoChangedRag] = useState<RagRating | null>(null);
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the pending banner/timer and reset the row flash in one call,
  // called both on next mutation start and on the auto-dismiss timeout.
  const clearBanner = useCallback(() => {
    if (bannerTimerRef.current !== null) {
      clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = null;
    }
    setRagBanner(null);
    setRecentlyAutoChangedId(null);
    setRecentlyAutoChangedRag(null);
  }, []);

  // Show a RAG auto-change banner and schedule its auto-dismiss after 5s.
  // Clears any previous pending timer first so back-to-back actions
  // replace rather than stack. Callers must capture prevRagRating before
  // the mutation and pass the mutation response as `next`.
  const showRagBanner = useCallback(
    (message: string, id: string, rag: RagRating | null) => {
      setRagBanner({ message });
      setRecentlyAutoChangedId(id);
      setRecentlyAutoChangedRag(rag);
      bannerTimerRef.current = setTimeout(clearBanner, 5000);
    },
    [clearBanner],
  );

  // Cleanup on unmount so the deferred setState never fires on a ghost component.
  useEffect(() => {
    return () => {
      if (bannerTimerRef.current !== null) {
        clearTimeout(bannerTimerRef.current);
      }
    };
  }, []);

  // B1: the table now reads from the contract-wide hook so that
  // client filter / sort / search / pagination operate over the FULL
  // contract — not the first 25 rows of a cursor-paginated API page.
  // A separate "shared" hook would just wrap this same call, so the
  // tab consumes useAllDeliverablesForSummary directly.
  const contractQuery = useManagedContract(contractId);
  const contractEndDate = contractQuery.data?.endDate;

  const summaryQuery = useAllDeliverablesForSummary(contractId);
  const items: Deliverable[] = useMemo(
    () =>
      (summaryQuery.data ?? []).filter((d) =>
        urlState.filters.includeArchived ? true : !d.archivedAt,
      ),
    [summaryQuery.data, urlState.filters.includeArchived],
  );
  const isPending = summaryQuery.isPending;
  const error = summaryQuery.error;

  // Pipeline order is archive → scope → SummaryStrip → chip+search →
  // sort → paginate. Scope is an explicit, visible user choice of slice
  // (All / Contract / SOW), so it shapes BOTH the strip counts and the
  // table beneath. Chip filters layer on top after the strip per the
  // existing "strip counts pre-chip" rule.
  const scopedItems = useMemo(
    () => applyScopeFilter(items, urlState.scope),
    [items, urlState.scope],
  );

  const groupedRows: Row[] = useMemo(
    () => groupDeliverables(scopedItems),
    [scopedItems],
  );

  // Count orphan rows so the tab can render the warning
  // banner above the table. Counted from the unfiltered grouped set
  // so the banner stays accurate even when the user has narrowed
  // the visible table.
  const orphanCount = useMemo(
    () => groupedRows.filter((r) => r.kind === "orphan").length,
    [groupedRows],
  );

  // Deduped + sorted clause-reference suggestions for
  // the typeahead in the add modal. Sourced from every deliverable
  // on the contract (active + archived); the typeahead's free-text
  // fallback handles brand-new clause references the user introduces.
  const clauseSuggestions = useMemo(() => {
    const set = new Set<string>();
    for (const d of items) {
      const ref = d.clauseReference?.trim();
      if (ref) set.add(ref);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  // Pipeline: summary-card filter → chip-bar filter + search → sort.
  const summaryFilteredRows = useMemo(
    () => applySummaryFilter(groupedRows, activeFilter),
    [groupedRows, activeFilter],
  );
  const chipFilteredRows = useMemo(
    () =>
      applyChipFilters(
        summaryFilteredRows,
        urlState.filters,
        urlState.search,
      ),
    [summaryFilteredRows, urlState.filters, urlState.search],
  );
  const sortedRows = useMemo(
    () =>
      sortRows(
        chipFilteredRows,
        deliverableSort.sortState.column,
        deliverableSort.sortState.direction,
      ),
    [chipFilteredRows, deliverableSort.sortState],
  );

  // Grouped-view row set: keep the parent/child/one-off Row shape so
  // recurring parents distribute alongside their children via the
  // surfaced-child rule. Sort by status-driven groups happens inside
  // groupRowsByStatus, so the per-column sort is ignored in grouped
  // mode.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const statusGroups = useMemo(
    () => groupRowsByStatus(chipFilteredRows, todayIso),
    [chipFilteredRows, todayIso],
  );

  // Pagination unit is the SCHEDULE (top-level row); children
  // inside a parent render in full once that parent is on the
  // current page. Page > totalPages clamps silently to the last
  // valid page.
  const totalSchedules = sortedRows.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalSchedules / urlState.pageSize),
  );
  const safePage = Math.min(Math.max(1, urlState.page), totalPages);
  const pagedRows = useMemo(() => {
    const from = (safePage - 1) * urlState.pageSize;
    return sortedRows.slice(from, from + urlState.pageSize);
  }, [sortedRows, safePage, urlState.pageSize]);
  const totalDeliverables = useMemo(() => {
    // Parent rows represent a recurring schedule, not a deliverable
    // themselves — only the children are deliverables. One-off rows ARE
    // the deliverable. Counting the parent + its children produces an
    // off-by-one against the tab badge (which counts the actual
    // deliverable items, not the schedules).
    let count = 0;
    for (const row of sortedRows) {
      if (row.kind === "parent") {
        count += row.children.length;
      } else {
        count += 1;
      }
    }
    return count;
  }, [sortedRows]);

  // SummaryStrip + the table now share one fetch — `summaryQuery`
  // above. The strip respects the scope view (All / Contract / SOW) so
  // its counts describe the slice the user is staring at; chip + search
  // filters are NOT applied (the strip remains the unfiltered baseline
  // within the chosen scope). archived rows are excluded — same rule
  // as the table's `items` derivation.
  const summaryDeliverables = useMemo(
    () =>
      applyScopeFilter(
        (summaryQuery.data ?? []).filter((d) => !d.archivedAt),
        urlState.scope,
      ),
    [summaryQuery.data, urlState.scope],
  );

  const milestonesQuery = useTrackingItems(contractId, "milestone", {
    limit: 200,
    includeArchived: false,
  });
  const milestones: Milestone[] = useMemo(
    () =>
      (milestonesQuery.data?.items ?? []).filter(
        (i): i is Milestone => i.type === "milestone",
      ),
    [milestonesQuery.data],
  );

  // SOWs are loaded contract-wide so the inline expand can resolve a
  // deliverable's stored `sowId` to the SOW's number/name caption.
  // includeArchived=true mirrors VariationSowTab so a deliverable
  // pinned to a since-archived SOW still resolves on read.
  const sowsQuery = useTrackingItems(contractId, "sow", {
    limit: 200,
    includeArchived: true,
  });
  const sows: Sow[] = useMemo(
    () =>
      (sowsQuery.data?.items ?? []).filter(
        (i): i is Sow => i.type === "sow",
      ),
    [sowsQuery.data],
  );
  const milestonesById = useMemo(() => {
    const map: Record<string, { id: string; name: string }> = {};
    for (const m of milestones) map[m.id] = { id: m.id, name: m.name };
    return map;
  }, [milestones]);

  // F3: secondary fetch for a deep-linked item that is genuinely absent from
  // the loaded list (deleted, or archived while the filter is off). `items` is
  // the whole contract list — so `deepLinkInList` true means the page-jump above
  // reveals it; the banner fires only when it's truly not in the list.
  const deepLinkInList = itemIdFromUrl
    ? items.some((i) => i.id === itemIdFromUrl)
    : false;
  const missingItemQuery = useTrackingItem(
    contractId,
    "deliverable",
    !deepLinkInList && itemIdFromUrl ? itemIdFromUrl : "",
  );
  const missingItem =
    missingItemQuery.data && missingItemQuery.data.type === "deliverable"
      ? (missingItemQuery.data as Deliverable)
      : null;

  const createMut = useCreateTrackingItem();
  const updateMut = useUpdateTrackingItem();
  const archiveMut = useArchiveTrackingItem();
  const generateMut = useGenerateOccurrences();

  // Deep-link page-jump: when ?item=<id> targets a deliverable on a later page,
  // jump the client-side paginator to the SCHEDULE row that holds it (the
  // pagination unit is the top-level row). A linked occurrence that is a child
  // of a recurring parent resolves to its parent's index, and that parent is
  // auto-expanded so the child is visible. Otherwise the row is highlighted
  // off-screen and the link appears to do nothing. The target row's own view
  // detail expand is opened too, so the linked record is readable without a
  // second click.
  const deepLinkTarget = findDeliverableDeepLinkTarget(
    itemIdFromUrl,
    sortedRows,
  );
  useDeepLinkPageJump({
    itemId: itemIdFromUrl,
    targetIndex: deepLinkTarget.index,
    pageSize: urlState.pageSize,
    onJump: (targetPage, _index, itemId) => {
      if (targetPage !== urlState.page) {
        updateUrl((s) => ({ ...s, page: targetPage }));
      }
      const parentId = deepLinkTarget.expandParentId;
      if (parentId) {
        setExpandedScheduleIds((prev) => new Set(prev).add(parentId));
      }
      setHighlightRowId(itemId);
      // deepLinkTarget.item is non-null whenever the jump fires (the hook
      // waits for index >= 0); opening view mode mirrors handleRowClick.
      setInlineEditItem(null);
      setInlineEditIsParent(false);
      setViewExpandedItem(deepLinkTarget.item);
    },
  });

  const handleOpenCreate = () => {
    setModalSource(undefined);
    setModalMode("create");
  };
  const handleCloseModal = () => {
    setModalMode(null);
    setPendingCreateSowId(null);
  };

  const handleCreate = async (input: CreateDeliverableInput) => {
    const created = (await createMut.mutateAsync({
      contractId,
      type: "deliverable",
      input,
    })) as Deliverable | undefined;
    if (created?.id) setHighlightRowId(created.id);
    if (created && shouldAutoOpenOnCreate(created)) {
      setGenerateTarget(created);
    }
  };

  const handleInlineUpdate = (item: Deliverable) => async (
    input: UpdateDeliverableInput,
  ) => {
    await updateMut.mutateAsync({
      contractId,
      type: "deliverable",
      itemId: item.id,
      input,
    });
    setInlineEditItem(null);
    setInlineEditIsParent(false);
    if (shouldAutoOpenOnUpdate(item.recurrenceRule, input)) {
      setGenerateTarget({ ...item, recurrenceRule: input.recurrenceRule });
    }
  };

  const handleArchiveConfirm = async (reason: string) => {
    if (!archiveTarget) return;
    await archiveMut.mutateAsync({
      contractId,
      type: "deliverable",
      itemId: archiveTarget.id,
      reason,
    });
    setArchiveTarget(null);
  };

  const handleGenerateConfirm = async (archiveOrphanIds?: string[]) => {
    if (!generateTarget) return;
    if (archiveOrphanIds && archiveOrphanIds.length > 0) {
      for (const orphanId of archiveOrphanIds) {
        await archiveMut.mutateAsync({
          contractId,
          type: "deliverable",
          itemId: orphanId,
          reason: "Superseded by updated recurrence rule",
        });
      }
    }
    await generateMut.mutateAsync({
      contractId,
      type: "deliverable",
      itemId: generateTarget.id,
    });
    setGenerateTarget(null);
  };

  const handleToggleExpand = useCallback((parentId: string) => {
    setExpandedScheduleIds((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  }, []);

  const handleExpandAll = () => {
    const all = new Set<string>();
    for (const row of groupedRows) {
      if (row.kind === "parent") all.add(row.deliverable.id);
    }
    setExpandedScheduleIds(all);
  };

  const handleCollapseAll = () => {
    setExpandedScheduleIds(new Set());
  };

  // Row click → view-mode expand toggle. Clicking the same row again
  // collapses the view; clicking a different row moves the expansion.
  const handleRowClick = useCallback((row: { deliverable: Deliverable }) => {
    const item = row.deliverable;
    setInlineEditItem(null);
    setInlineEditIsParent(false);
    setViewExpandedItem((prev) => (prev?.id === item.id ? null : item));
  }, []);

  const handleAction = useCallback((row: Row, action: DeliverableAction) => {
    const item = row.deliverable;
    if (action === "archive") {
      setArchiveTarget(item);
      return;
    }
    if (action === "primary") {
      // Parent rows still open the Generate Occurrences flow.
      if (row.kind === "parent") {
        setGenerateTarget(item);
        return;
      }
      // Routing matrix:
      //   not-started / overdue → legacy MarkPopover ("Mark submitted")
      //     — entry edge with no gate decisions; supplier chase queue.
      //   submitted / under-review / late / conditionally-accepted /
      //     awaiting-resubmission → AcceptanceGateDialog
      //     — customer-decision gate (accept / conditionally-accept /
      //       fail / reject / supplier-resubmits).
      // Other states (revision-requested) have no primary affordance
      // (contextPrimaryFor returns null upstream).
      const derived = deriveDeliverableStatus(
        item.status,
        item.dueDate,
        new Date(),
        item.submittedAt,
      );
      setMarkError(null);
      if (derived === "not-started" || derived === "overdue") {
        setMarkPopover({ item, action: "submit" });
        return;
      }
      if (
        derived === "submitted" ||
        derived === "under-review" ||
        derived === "late" ||
        derived === "conditionally-accepted" ||
        derived === "awaiting-resubmission"
      ) {
        setAcceptanceGateTarget(item);
        return;
      }
      return;
    }
    if (action === "edit") {
      setViewExpandedItem(null);
      setInlineEditItem((prev) => {
        if (prev?.id === item.id) {
          setInlineEditIsParent(false);
          return null;
        }
        setInlineEditIsParent(row.kind === "parent");
        return item;
      });
    }
  }, []);

  const handleSummaryFilterChange = (filter: SummaryFilterKey | null) => {
    setActiveFilter((prevFilter) => {
      const nextFilter = filter;
      if (nextFilter && !prevFilter) {
        setPreFilterExpandedScheduleIds(expandedScheduleIds);
        const parentIdsWithMatches = applySummaryFilter(
          groupedRows,
          nextFilter,
        )
          .filter((r) => r.kind === "parent")
          .map((r) => r.deliverable.id);
        setExpandedScheduleIds(new Set(parentIdsWithMatches));
      } else if (!nextFilter && prevFilter) {
        if (preFilterExpandedScheduleIds) {
          setExpandedScheduleIds(preFilterExpandedScheduleIds);
          setPreFilterExpandedScheduleIds(null);
        }
      } else if (nextFilter && prevFilter && nextFilter !== prevFilter) {
        const parentIdsWithMatches = applySummaryFilter(
          groupedRows,
          nextFilter,
        )
          .filter((r) => r.kind === "parent")
          .map((r) => r.deliverable.id);
        setExpandedScheduleIds(new Set(parentIdsWithMatches));
      }
      return nextFilter;
    });
    // Filter change always resets pagination back to page 1.
    updateUrl((s) => ({ ...s, page: 1 }));
  };

  // F6: any chip-filter signal (status / owner / rag / due chips —
  // not includeArchived, which is a binary visibility toggle) is
  // considered "active" for the auto-expand pathway.
  const hasActiveChipFilter = (
    f: typeof urlState.filters,
  ): boolean =>
    f.status.length > 0 ||
    f.owner.length > 0 ||
    f.rag.length > 0 ||
    f.due.length > 0;

  const handleChipFiltersChange = (
    next: typeof urlState.filters,
  ) => {
    const wasActive = hasActiveChipFilter(urlState.filters);
    const isActive = hasActiveChipFilter(next);
    if (!wasActive && isActive) {
      // Transitioning to chip-filter active: snapshot current
      // expansion + auto-expand parents that have at least one
      // matching child under the new chip predicate.
      setPreChipFilterExpandedScheduleIds(expandedScheduleIds);
      const parentIdsWithMatches = applyChipFilters(
        groupedRows,
        next,
        urlState.search,
      )
        .filter((r) => r.kind === "parent")
        .map((r) => r.deliverable.id);
      setExpandedScheduleIds(new Set(parentIdsWithMatches));
    } else if (wasActive && !isActive) {
      // Chip filter cleared: restore the pre-filter set.
      if (preChipFilterExpandedScheduleIds) {
        setExpandedScheduleIds(preChipFilterExpandedScheduleIds);
        setPreChipFilterExpandedScheduleIds(null);
      }
    } else if (wasActive && isActive) {
      // Switching between chip selections: re-auto-expand for the
      // new predicate while preserving the saved pre-filter snapshot
      // for an eventual full clear.
      const parentIdsWithMatches = applyChipFilters(
        groupedRows,
        next,
        urlState.search,
      )
        .filter((r) => r.kind === "parent")
        .map((r) => r.deliverable.id);
      setExpandedScheduleIds(new Set(parentIdsWithMatches));
    }
    updateUrl((s) => ({ ...s, filters: next, page: 1 }));
  };

  const handleSearchChange = (next: string) => {
    updateUrl((s) => ({ ...s, search: next, page: 1 }));
  };

  // PO5: clear every chip + the search box in one click. Also
  // resets pagination so the user lands on page 1 of the
  // un-narrowed list.
  const handleClearAll = () => {
    updateUrl((s) => ({
      ...s,
      filters: {
        status: [],
        owner: [],
        rag: [],
        due: [],
        sow: [],
        milestone: [],
        includeArchived: false,
        hasFailedAcceptance: false,
        notAccepted: false,
      },
      search: "",
      page: 1,
    }));
    // Also clear the SummaryStrip card filter and restore expansion
    // snapshots so the user lands on the un-narrowed list.
    setActiveFilter(null);
    if (preFilterExpandedScheduleIds) {
      setExpandedScheduleIds(preFilterExpandedScheduleIds);
      setPreFilterExpandedScheduleIds(null);
    }
    if (preChipFilterExpandedScheduleIds) {
      setExpandedScheduleIds(preChipFilterExpandedScheduleIds);
      setPreChipFilterExpandedScheduleIds(null);
    }
    setFilterResetCounter((c) => c + 1);
  };

  // SortIndicator (the sortable column headers) computes the next sort
  // state itself and reports it here fully-formed — this tab's job is
  // just to persist it. deliverableSort's onChange (declared above) is
  // where the URL write + aria-live announcement actually happen.
  const handleSortChange = (change: {
    column: DeliverableSortColumn | null;
    direction: DeliverableSortDirection;
  }) => {
    deliverableSort.setSortState({
      column: change.column,
      direction: change.direction,
      cleared: change.column === null,
    });
  };

  const handlePageChange = (page: number) => {
    updateUrl((s) => ({ ...s, page }));
  };

  const handlePageSizeChange = (size: number) => {
    updateUrl((s) => ({ ...s, pageSize: size, page: 1 }));
  };

  // Export the CURRENT filtered + searched + sorted set (all matching
  // rows, not just the visible page) to CSV. Leaf deliverables only —
  // the synthetic recurring-parent schedule rows are excluded by the
  // helper. Best-effort: a Blob/anchor failure shouldn't break the tab.
  const handleExportCsv = useCallback(() => {
    const sowsById: Record<string, string> = {};
    for (const s of sows) {
      sowsById[s.id] = `${formatSowDisplayNumber(s.number)} · ${s.name}`;
    }
    const milestoneNamesById: Record<string, string> = {};
    for (const m of milestones) milestoneNamesById[m.id] = m.name;

    const csv = buildDeliverablesCsv(sortedRows, {
      today: new Date(),
      sowsById,
      milestonesById: milestoneNamesById,
    });
    const contract = contractQuery.data;
    const filename = deliverablesCsvFilename(
      {
        id: contractId,
        contractNumber: contract?.contractNumber,
        title: contract?.title,
      },
      new Date(),
    );
    try {
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Swallow — CSV export is best-effort and not a core flow.
    }
  }, [sortedRows, sows, milestones, contractQuery.data, contractId]);

  // B1: server-side cursor pagination is gone — the contract-wide
  // hook above already loops nextToken until exhausted, and the new
  // client-side <Pagination> below paginates the resulting flat
  // list. The TrackingTabShell's optional `paginationState` is
  // therefore omitted to avoid rendering a second Prev/Next pair.

  // If the URL specified a page beyond totalPages (e.g. ?page=99 on a
  // 2-page list), silently rewrite the URL to the clamped page so the
  // user's address bar matches the rendered state on next interaction.
  useEffect(() => {
    if (urlState.page > totalPages) {
      updateUrl((s) => ({ ...s, page: totalPages }));
    }
  }, [urlState.page, totalPages, updateUrl]);

  // Cross-tab "Add deliverable to this SOW" flow: when the URL carries
  // `?action=create&scope=sow:<id>`, open the create modal with the SOW
  // pre-selected, then strip ONLY `action` so the user lands in SOW
  // view after closing. `scope=sow:<id>` survives the navigation. Without
  // a valid SOW resolvable from scope, just strip `action` and skip the
  // pre-fill — the broken-link guard below handles the case where
  // `scope=sow:<unknown>` strips scope itself.
  useSowScopedCreateAction({
    searchParams,
    setSearchParams,
    scopeSowId: urlState.scope.mode === "sow" ? urlState.scope.sowId : null,
    sowsLoaded: sowsQuery.data !== undefined,
    sows,
    onTrigger: (sowId) => {
      setPendingCreateSowId(sowId);
      setModalMode("create");
    },
  });

  // Broken-link guard: `?scope=sow:<unknown-or-archived-or-cancelled>`
  // silently falls back to All and strips the param after the SOW list
  // settles. Mirrors the existing pattern used for the legacy `sowId`
  // pre-fill flow.
  useEffect(() => {
    const scope = urlState.scope;
    if (scope.mode !== "sow") return;
    if (sowsQuery.data === undefined) return;
    const target = sows.find((s) => s.id === scope.sowId);
    const isUsable =
      target && !target.archivedAt && target.status !== "cancelled";
    if (!isUsable) {
      updateUrl((s) => ({ ...s, scope: ALL_SCOPE, page: 1 }));
    }
  }, [urlState.scope, sows, sowsQuery.data, updateUrl]);

  const handleScopeChange = (next: ScopeValue) => {
    updateUrl((s) => {
      // When transitioning out of All view, clear any stale SOW chip
      // selection so an invisible filter cannot survive the switch.
      const nextSow =
        next.mode === "all" ? s.filters.sow : [];
      return {
        ...s,
        scope: next,
        filters: { ...s.filters, sow: nextSow },
        page: 1,
      };
    });
  };

  // Shared row-expand renderer. `withOccurrenceContext` is true for the
  // grouped view so the detail panel can surface parent/sibling occurrence
  // context; false for the sequential table which exposes the full
  // occurrence list inline and doesn't need it.
  const makeExpandedRowRenderer =
    (withOccurrenceContext: boolean) =>
    (deliverable: Deliverable) =>
      inlineEditItem && inlineEditItem.id === deliverable.id ? (
        <DeliverableInlineExpand
          key={deliverable.id}
          item={deliverable}
          contractId={contractId}
          milestones={milestones}
          sows={sows}
          mode={inlineEditIsParent ? "parent" : undefined}
          onUpdate={handleInlineUpdate(deliverable)}
          onCancel={() => {
            setInlineEditItem(null);
            setInlineEditIsParent(false);
          }}
          isSubmitting={updateMut.isPending}
        />
      ) : (
        <DeliverableDetailExpand
          key={deliverable.id}
          item={deliverable}
          milestones={milestones}
          {...(withOccurrenceContext
            ? lookupOccurrenceContext(deliverable, summaryQuery.data ?? [])
            : {})}
          onEdit={
            deliverable.archivedAt
              ? undefined
              : () => {
                  setViewExpandedItem(null);
                  setInlineEditItem(deliverable);
                  setInlineEditIsParent(!!deliverable.recurrenceRule);
                }
          }
          onNavigateToMilestone={onNavigateToMilestone}
          onReopenRequested={
            deliverable.archivedAt
              ? undefined
              : () => setAcceptanceGateTarget(deliverable)
          }
        />
      );

  return (
    <div className="space-y-4">
      <div
        data-testid="deliverable-sort-announcer"
        role="status"
        aria-live="polite"
        className="sr-only"
      >
        {sortAnnouncement}
      </div>
      {itemIdFromUrl && !deepLinkInList && missingItem && archiveBanner && (
        <Alert
          variant="info"
          onDismiss={() => setArchiveBanner(false)}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span>
              {missingItem.archivedAt
                ? `This deliverable / obligation was archived on ${formatDayMonthYear(
                    missingItem.archivedAt.slice(0, 10),
                  )}.`
                : `This deliverable / obligation is not shown in the current list.`}
            </span>
            <button
              type="button"
              className="underline"
              onClick={() => setDetailItem(missingItem)}
            >
              View archived item
            </button>
          </div>
        </Alert>
      )}

      {itemIdFromUrl &&
        !deepLinkInList &&
        missingItemQuery.error &&
        archiveBanner && (
          <Alert variant="info" onDismiss={() => setArchiveBanner(false)}>
            Item not found. It may have been deleted.
          </Alert>
        )}

      <ScopeSelector
        scope={urlState.scope}
        onScopeChange={handleScopeChange}
        sows={sows}
        itemNoun="deliverable"
      />

      <SummaryStrip
        deliverables={summaryDeliverables}
        onFilterChange={handleSummaryFilterChange}
        activeFilter={activeFilter}
      />

      {orphanCount > 0 && (
        <div
          data-testid="deliverable-orphan-banner"
          role="status"
          className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-caption text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span>
            {orphanCount === 1
              ? "1 occurrence belongs"
              : `${orphanCount} occurrences belong`}{" "}
            to a schedule that is no longer visible.
          </span>
        </div>
      )}

      <ActiveFilterSummary
        filters={urlState.filters}
        search={urlState.search}
        summaryFilter={activeFilter}
        onClearAll={handleClearAll}
        sows={sows}
        milestones={milestones}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterChipBar
          filters={urlState.filters}
          onFiltersChange={handleChipFiltersChange}
          search={urlState.search}
          onSearchChange={handleSearchChange}
          sows={sows}
          milestones={milestones}
          resetCounter={filterResetCounter}
          showSowChip={urlState.scope.mode === "all"}
        />
        <div className="flex items-center gap-3">
          {/* Ghost variant so the export affordance stays quiet against
           *  the page primary (+ Add deliverable, in the tab header). */}
          <Button
            variant="ghost"
            size="sm"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExportCsv}
            data-testid="deliverable-export-csv"
          >
            Export CSV
          </Button>
          <TableViewToggle
            value={urlState.view}
            ariaLabel="Deliverable table view"
            onChange={(next) =>
              updateUrl((s) => ({ ...s, view: next, page: 1 }))
            }
          />
          <button
            type="button"
            onClick={handleExpandAll}
            className="text-caption text-slate-600 hover:text-slate-900 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-300 dark:hover:text-slate-100"
          >
            Expand all
          </button>
          <span aria-hidden="true" className="text-slate-300 dark:text-slate-600">
            ·
          </span>
          <button
            type="button"
            onClick={handleCollapseAll}
            className="text-caption text-slate-600 hover:text-slate-900 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-300 dark:hover:text-slate-100"
          >
            Collapse all
          </button>
        </div>
      </div>

      {/* 5-state legend explaining the OccurrenceStrip tile vocabulary.
          Sits ABOVE the table, right-aligned, to mirror the milestone
          tab's ProgressDotsKey placement exactly (consistency across
          tracking tabs). Defaults dismissed; the user can recall it on
          demand and the choice persists per-user. */}
      <div className="mb-2 flex justify-end">
        <OccurrenceStripKey />
      </div>

      {/* aria-live banner for RAG auto-changes. Mirrors the sort
          announcer pattern above but uses a visible region (not sr-only)
          so sighted users also see the feedback. Clears automatically
          after 5s or on the next lifecycle mutation. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="rag-auto-change-banner"
      >
        {ragBanner && (
          <p className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-100 px-3 py-2 text-body-sm text-emerald-700 border-l-4 border-l-emerald-500 dark:border-emerald-700/60 dark:bg-emerald-900/30 dark:text-emerald-400">
            {ragBanner.message}
          </p>
        )}
      </div>

      <TrackingTabShell
        type="deliverable"
        onAdd={handleOpenCreate}
        includeArchived={urlState.filters.includeArchived}
        onToggleArchived={(v) => {
          handleChipFiltersChange({
            ...urlState.filters,
            includeArchived: v,
          });
        }}
        isEmpty={!isPending && !error && items.length === 0}
        showIncludeArchived={false}
      >
        {urlState.view === "grouped" ? (
          <DeliverableGroupedView
            groups={statusGroups}
            expandedScheduleIds={expandedScheduleIds}
            onToggleExpand={handleToggleExpand}
            onRowClick={handleRowClick}
            onAction={handleAction}
            highlightRowId={highlightRowId}
            ragFlashRowId={recentlyAutoChangedId}
            ragFlashRagRating={recentlyAutoChangedRag}
            milestonesById={milestonesById}
            expandedItemId={
              inlineEditItem?.id ?? viewExpandedItem?.id ?? null
            }
            renderExpandedRow={makeExpandedRowRenderer(true)}
          />
        ) : (
          <>
            <DeliverableTable
              rows={pagedRows}
              expandedScheduleIds={expandedScheduleIds}
              onToggleExpand={handleToggleExpand}
              onRowClick={handleRowClick}
              onAction={handleAction}
              highlightRowId={highlightRowId}
              ragFlashRowId={recentlyAutoChangedId}
              ragFlashRagRating={recentlyAutoChangedRag}
              milestonesById={milestonesById}
              sortColumn={deliverableSort.sortState.column}
              sortDirection={deliverableSort.sortState.direction}
              onSortChange={handleSortChange}
              expandedItemId={
                inlineEditItem?.id ?? viewExpandedItem?.id ?? null
              }
              renderExpandedRow={makeExpandedRowRenderer(false)}
            />
            <TrackingPagination
              page={safePage}
              pageSize={urlState.pageSize}
              total={totalSchedules}
              noun="schedule"
              secondaryCount={{ value: totalDeliverables, noun: "deliverable" }}
              testIdPrefix="deliverable"
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </TrackingTabShell>

      <DeliverableFormModal
        isOpen={modalMode === "create"}
        onClose={handleCloseModal}
        mode="create"
        contractId={contractId}
        milestones={milestones}
        sows={sows}
        clauseSuggestions={clauseSuggestions}
        initialSowId={pendingCreateSowId ?? undefined}
        onCreate={handleCreate}
        isSubmitting={createMut.isPending}
        contractEndDate={contractEndDate}
      />
      <DeliverableFormModal
        isOpen={modalMode === "edit"}
        onClose={handleCloseModal}
        mode="edit"
        contractId={contractId}
        source={modalSource}
        milestones={milestones}
        sows={sows}
        clauseSuggestions={clauseSuggestions}
        onUpdate={async (input) => {
          if (!modalSource) return;
          await updateMut.mutateAsync({
            contractId,
            type: "deliverable",
            itemId: modalSource.id,
            input,
          });
        }}
        isSubmitting={updateMut.isPending}
        contractEndDate={contractEndDate}
      />

      <ArchiveDialog
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchiveConfirm}
        itemLabel={
          archiveTarget
            ? `deliverable / obligation "${archiveTarget.name}"`
            : "this deliverable / obligation"
        }
        isSubmitting={archiveMut.isPending}
      />

      <GenerateOccurrencesDialog
        isOpen={!!generateTarget}
        onClose={() => setGenerateTarget(null)}
        contractId={contractId}
        item={generateTarget}
        onConfirm={handleGenerateConfirm}
        isSubmitting={generateMut.isPending}
      />

      <DeliverableDetailModal
        isOpen={!!detailItem}
        onClose={() => setDetailItem(null)}
        item={detailItem}
      />

      {acceptanceGateTarget && (
        <AcceptanceGateDialog
          isOpen={true}
          onClose={() => setAcceptanceGateTarget(null)}
          title={`Update acceptance — ${acceptanceGateTarget.name}`}
          derivedStatus={toAcceptanceGateStatus(acceptanceGateTarget)}
          failedAcceptanceCount={
            acceptanceGateTarget.failedAcceptanceCount ?? 0
          }
          minDate={deriveAcceptanceGateMinDate(acceptanceGateTarget)}
          todayIso={new Date().toISOString().slice(0, 10)}
          onSubmit={async (target, payload) => {
            const newEvent: DeliverableLifecycleEvent = {
              type: target,
              date: payload.date,
              ...(payload.comment ? { comment: payload.comment } : {}),
              recordedAt: new Date().toISOString(),
              recordedBy: authUser?.userId ?? "unknown",
            };
            const prevRagRating = acceptanceGateTarget.ragRating;
            clearBanner();
            const next = (await updateMut.mutateAsync({
              contractId,
              type: "deliverable",
              itemId: acceptanceGateTarget.id,
              input: {
                version: acceptanceGateTarget.version,
                lifecycleEvents: [newEvent],
              },
            })) as TrackingItem;
            if (next.type === "deliverable") {
              const bannerMessage = deriveRagBannerReason(prevRagRating, next);
              if (bannerMessage !== null) {
                showRagBanner(bannerMessage, next.id, next.ragRating ?? null);
              }
            }
            setAcceptanceGateTarget(null);
          }}
          onReopen={async () => {
            const newEvent: DeliverableLifecycleEvent = {
              type: "reverted",
              date: new Date().toISOString().slice(0, 10),
              recordedAt: new Date().toISOString(),
              recordedBy: authUser?.userId ?? "unknown",
            };
            await updateMut.mutateAsync({
              contractId,
              type: "deliverable",
              itemId: acceptanceGateTarget.id,
              input: {
                version: acceptanceGateTarget.version,
                lifecycleEvents: [newEvent],
              },
            });
            setAcceptanceGateTarget(null);
          }}
        />
      )}

      {markPopover && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center bg-slate-900/30 px-4 pt-24"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setMarkPopover(null);
              setMarkError(null);
            }
          }}
        >
          <div className="relative w-full max-w-sm">
            <MarkDeliverableDatePopover
              action={markPopover.action}
              deliverableName={markPopover.item.name}
              // Accept/reject must not predate the submission. Pull the
              // anchor from the stored submittedAt (date portion); for
              // the submit action there's no anchor yet.
              minEffectiveDate={
                markPopover.action === "submit"
                  ? undefined
                  : markPopover.item.submittedAt?.slice(0, 10)
              }
              isPending={updateMut.isPending}
              errorMessage={markError}
              onCancel={() => {
                setMarkPopover(null);
                setMarkError(null);
              }}
              onConfirm={async ({ effectiveDate, comment }) => {
                const target = markPopover;
                // Mark submitted now routes through the lifecycleEvents append
                // path (the single source of truth) rather than the legacy
                // bare-`status` write. The event is intent-only — the server
                // stamps provenance (recordedBy/recordedAt) and the
                // submittedAt timestamp; the client sends type + business date
                // (+ optional comment). Submit is the only live mark action;
                // accept/reject route through the AcceptanceGate.
                // Capture prevItem from caller scope before the mutation
                // fires. The React Query detail cache is only populated for
                // rows the user has explicitly opened, so cache-only
                // capture would silently no-op for collapsed-row actions.
                const prevRagRating = target.item.ragRating;
                // Clear any pending banner from a prior mutation so
                // back-to-back actions replace rather than stack.
                clearBanner();
                try {
                  const next = (await updateMut.mutateAsync({
                    contractId,
                    type: "deliverable",
                    itemId: target.item.id,
                    input: {
                      version: target.item.version,
                      lifecycleEvents: [
                        {
                          type: "submitted",
                          date: effectiveDate,
                          ...(comment ? { comment } : {}),
                        },
                      ],
                    },
                  })) as TrackingItem;
                  setMarkPopover(null);
                  setMarkError(null);
                  // Compute auto-change banner from returned entity.
                  if (next.type === "deliverable") {
                    const bannerMessage = deriveRagBannerReason(prevRagRating, next);
                    if (bannerMessage !== null) {
                      // Store the RAG from the mutation response — the list
                      // query may not have re-fetched yet, so the row in the
                      // table could still have the old (or undefined) rating.
                      showRagBanner(bannerMessage, next.id, next.ragRating ?? null);
                    }
                  }
                } catch (err) {
                  setMarkError(
                    err instanceof Error
                      ? err.message
                      : "Failed to update deliverable.",
                  );
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
